"use client";

import { BetaPageHeader } from "@zapier/design-system";
import { useTranslations } from "@zapier/i18n";
import { useConnectionStats } from "@zapier/client";
import { ManagedConnectionSource } from "@zapier/shared";
import styles from "./ConnectionsHeader.module.css";

import { useCanCreateManagedConnection } from "../useCanCreateManagedConnection";
import { ConnectionsFilterBar } from "../ConnectionsFilters/ConnectionsFilterBar";
import { ConnectionsStatTiles } from "../ConnectionsStatTiles/ConnectionsStatTiles";
import { ManagedConnectionNudgeCard } from "../ManagedConnectionNudgeCard/ManagedConnectionNudgeCard";
import { useManagedConnectionNudgeDismissed } from "../ManagedConnectionNudgeCard/useManagedConnectionNudgeDismissed";
import { ConnectionsViewSelect } from "./ConnectionsViewSelect";
import { CreateConnectionAction } from "./CreateConnectionAction";

interface ConnectionsHeaderProperty {
  onAddConnection: () => void;
  onAddManagedConnection: (source: ManagedConnectionSource) => void;
  hideFilters?: boolean;
}

function ConnectionsHeader({
  onAddConnection,
  onAddManagedConnection,
  hideFilters = false,
}: ConnectionsHeaderProperty) {
  const t = useTranslations("connections-header");
  // Only admins+ on an account with the managed connections entitlement can
  // create managed (shared-credential) connections; everyone else gets the
  // plain "Create connection" button with no menu at all.
  const { canCreateManagedConnection, isLoading: isPermissionsLoading } =
    useCanCreateManagedConnection();

  const { isDismissed: isNudgeCardDismissed, dismiss: dismissNudgeCard } =
    useManagedConnectionNudgeDismissed();
  const {
    managed: managedConnectionCount,
    total: totalConnectionCount,
    isLoading: isConnectionStatsLoading,
  } = useConnectionStats();
  // Distinct from the true-zero-connections empty state: shows only once at
  // least one connection exists and none of them are managed yet.
  const showManagedConnectionNudgeCard =
    canCreateManagedConnection &&
    !isConnectionStatsLoading &&
    managedConnectionCount === 0 &&
    !!totalConnectionCount &&
    !isNudgeCardDismissed;

  return (
    <BetaPageHeader
      title={t("headings.page-title")}
      actions={
        <CreateConnectionAction
          isLoading={isPermissionsLoading}
          showManagedOption={canCreateManagedConnection}
          onAddConnection={onAddConnection}
          onAddManagedConnection={onAddManagedConnection}
        />
      }
      additionalContent={
        hideFilters ? undefined : (
          <div className={styles.additionalContentStack}>
            {showManagedConnectionNudgeCard ? (
              <ManagedConnectionNudgeCard
                onCreateManagedConnection={() =>
                  onAddManagedConnection(ManagedConnectionSource.NudgeCard)
                }
                onDismiss={dismissNudgeCard}
              />
            ) : null}
            {/* Gated the same way as the Managed option in
            CreateConnectionAction - these counts are meaningless to accounts
            that can't create managed connections in the first place. */}
            {canCreateManagedConnection ? <ConnectionsStatTiles /> : null}
            <div className={styles.midRow}>
              <ConnectionsViewSelect />
              <div className={styles.filterBar}>
                <ConnectionsFilterBar showFiltersButton />
              </div>
            </div>
          </div>
        )
      }
    />
  );
}

export default ConnectionsHeader;
